import { LbNotificationExpiry } from '@electron/shared/lb-notification.types';
import {
  buildSkipEntry,
  type MeetingAutoStartSkipReason,
} from '@electron/shared/meeting-debug.types';
import { getProviderFromSegmentProviderName } from '@electron/shared/meetings.types';
import { config } from '@littlebird/sdk';
import { v4 as uuidv4 } from 'uuid';

import { electronApis } from '@/features/electron-api';
import { meetingDebugStore } from '@/features/meeting-debug/meeting-debug.store';
import { subscriptionStore } from '@/features/subscription/subscription.store';
import { logError } from '@/lib/errorLogging';
import { settingsStore } from '@/stores/settings.store';
import { userStore } from '@/stores/user.store';

import {
  type DetectedMeetingApp,
  isAnsweredMeeting,
  meetingAppDetections,
  withdrawMeetingAppOffer,
} from './meeting-app-detections';
import {
  findActiveCalendarEvent,
  isBlockedByTeamRecordingRules,
} from './meetings.query';
import { meetingStore } from './meetings.state';
import { getMeetingProvider, getProviderFromBundleId } from './meetings.types';

const NOTIFICATION_END_POLL_INTERVAL_MS = 1000;

type MeetingAppsUpdate = { apps: DetectedMeetingApp[]; detectionId?: string };

type AppsReportedBeforeListening =
  | 'not-listening'
  | 'asking'
  | 'superseded-by-live-report'
  | 'answered';

type PresenceLossStop =
  | { status: 'idle' }
  | { status: 'stopping' }
  | {
      status: 'notifying';
      notificationId: string;
      sendInFlight: boolean;
    };

class MeetingAppOffers {
  presenceLossStop: PresenceLossStop = { status: 'idle' };
  private connectedHooks = new Set<(update: MeetingAppsUpdate) => void>();
  private appsReportedBeforeListening: AppsReportedBeforeListening =
    'not-listening';

  connectDetectionHook(onAppsUpdated: (update: MeetingAppsUpdate) => void) {
    this.listenForAppsUpdates();
    this.connectedHooks.add(onAppsUpdated);
    void this.offerNext(undefined);
    return () => {
      this.connectedHooks.delete(onAppsUpdated);
    };
  }

  async offerNext(detectionId: string | undefined): Promise<void> {
    if (!this.hasConnectedHook || !nextAppToOffer()) return;

    const skip = (reason: MeetingAutoStartSkipReason) =>
      meetingDebugStore.record({
        ...buildSkipEntry('app_detection', reason),
        meetingId: null,
        detectionId,
      });

    if (config.meetings.disable_detection_notification) {
      skip('detection_notification_disabled');
      return;
    }
    if (meetingStore.recording.status !== 'idle') {
      skip('already_recording');
      return;
    }

    const presenceLossStop = this.presenceLossStop;
    if (presenceLossStop.status === 'stopping') {
      skip('presence_loss_stop_in_progress');
      return;
    }
    if (presenceLossStop.status === 'notifying') {
      const notificationId = presenceLossStop.notificationId;
      if (
        presenceLossStop.sendInFlight ||
        (await this.isPresenceLossNoticeUp(notificationId))
      ) {
        skip('presence_loss_notification_pending');
        return;
      }
      this.endPresenceLossNoticeIfCurrent(notificationId);
    }

    if (!this.hasConnectedHook) return;
    const holdReason = this.getOfferHoldReason();
    if (holdReason) {
      skip(holdReason);
      return;
    }

    const app = nextAppToOffer();
    if (!app) return;

    const appName = app.name ?? 'Meeting App';
    const notificationId = uuidv4();
    const autoStartAttemptId = uuidv4();
    meetingAppDetections.beginOffer(app, {
      notificationId,
      autoStartAttemptId,
    });

    const identityProvider = getProviderFromSegmentProviderName(
      app.meetingIdentity?.providerName
    );
    const meetingKey = app.meetingIdentity?.meetingKey;
    const calendarEvent = findActiveCalendarEvent(app.bundleIdentifier, {
      identity: meetingKey
        ? { provider: identityProvider, meetingKey }
        : undefined,
    });
    if (calendarEvent && (await isBlockedByTeamRecordingRules(calendarEvent))) {
      meetingAppDetections.endOffer(autoStartAttemptId);
      await this.offerNext(detectionId);
      return;
    }
    if (!meetingAppDetections.isCurrentOffer(autoStartAttemptId)) {
      skip(
        meetingStore.recording.status === 'idle'
          ? 'meeting_app_gone'
          : 'already_recording'
      );
      return;
    }
    if (!this.hasConnectedHook) {
      meetingAppDetections.cancelUnsentOffer(autoStartAttemptId);
      return;
    }

    const autoStartSetting = config.meetings.auto_start_on_detection;
    const meetingLimitReached = subscriptionStore.hasReachedMeetingLimit;
    const autoStartOnDetection =
      isAnsweredMeeting(app) && autoStartsOnDetection(app);
    if (autoStartOnDetection) {
      meetingAppDetections.allowAutoStart(autoStartAttemptId);
    }
    const candidateCount = meetingAppDetections.detectedAppCount;
    const websiteHostForInternalUsersOnly = userStore.isInternalUser
      ? (app.websiteHost ?? null)
      : null;

    // eslint-disable-next-line no-console -- PostHog captures renderer console.log breadcrumbs
    console.log('[meeting-auto-start] candidate_decision', {
      detection_id: detectionId ?? null,
      auto_start_attempt_id: autoStartAttemptId,
      notification_id: notificationId,
      auto_start_on_detection: autoStartSetting ?? null,
      app_name: appName,
      app_bundle_id: app.bundleIdentifier ?? null,
      website_host: websiteHostForInternalUsersOnly,
      candidate_count: candidateCount,
    });

    electronApis.meetings?.logAutoStartEvent?.('candidate_decision', {
      detection_id: detectionId ?? null,
      auto_start_attempt_id: autoStartAttemptId,
      notification_id: notificationId,
      auto_start_setting_at_detection: autoStartSetting,
      auto_start_setting_from_ref: autoStartSetting ?? null,
      recording_status: meetingStore.recording.status,
      app_name: appName,
      app_bundle_id: app.bundleIdentifier ?? null,
      website_host: websiteHostForInternalUsersOnly,
      candidate_count: candidateCount,
      meeting_limit_reached: meetingLimitReached,
    });

    const provider =
      getProviderFromBundleId(app.bundleIdentifier) ??
      identityProvider ??
      getMeetingProvider(calendarEvent);

    if (autoStartOnDetection && !settingsStore.lbNotificationsEnabled) {
      electronApis.meetings
        .requestStartMeetingNote({
          startReason: 'app_detection',
          appName,
          appBundleId: app.bundleIdentifier,
          provider,
          meetingKey,
          detectionId,
          autoStartAttemptId,
          notificationId,
          startAction: 'auto_start_without_notification',
        })
        .catch((error) => {
          logError(
            error,
            '[meeting-app-detection] Failed to request automatic recording'
          );
          void this.offerNext(undefined);
        });
      meetingAppDetections.endOffer(autoStartAttemptId);
      return;
    }

    console.info('[meeting-app-detection] sending notification', {
      appName,
      appBundleId: app.bundleIdentifier,
      provider,
      autoStartOnDetection,
      meetingLimitReached,
      calendarEventTitle: calendarEvent?.summary,
    });

    meetingAppDetections.markOfferSent(autoStartAttemptId);
    electronApis.lbNotifications.sendNotification({
      id: notificationId,
      view: 'meeting-detected',
      expiresAt: LbNotificationExpiry.alert(),
      delivery: autoStartOnDetection ? 'expedited' : 'queued',
      payload: {
        appName,
        appBundleId: app.bundleIdentifier,
        provider,
        meetingKey,
        detectionId,
        autoStartAttemptId,
        autoStartOnDetection,
        meetingLimitReached,
        calendarEventTitle: calendarEvent?.summary ?? undefined,
        websiteHost: app.websiteHost,
        isBrowser: app.isBrowser,
      },
      sound: 'ask',
    });
    this.pollForNotificationEnd(
      notificationId,
      () => meetingAppDetections.isCurrentOffer(autoStartAttemptId),
      async () => {
        meetingAppDetections.endOffer(autoStartAttemptId);
        await this.offerNext(undefined);
      }
    );

    electronApis.meetings?.logAutoStartEvent?.('dispatched', {
      detection_id: detectionId ?? null,
      auto_start_attempt_id: autoStartAttemptId,
      notification_id: notificationId,
      auto_start_dispatched: autoStartOnDetection ?? null,
      app_bundle_id: app.bundleIdentifier ?? null,
    });
  }

  resumeOffersAfterPresenceLossNotice(notificationId: string) {
    this.pollForNotificationEnd(
      notificationId,
      () => this.isPresenceLossNotice(notificationId),
      async () => {
        this.presenceLossStop = { status: 'idle' };
        await this.offerNext(undefined);
      }
    );
  }

  endPresenceLossNoticeIfCurrent(notificationId: string) {
    if (!this.isPresenceLossNotice(notificationId)) return false;
    this.presenceLossStop = { status: 'idle' };
    return true;
  }

  private get hasConnectedHook() {
    return this.connectedHooks.size > 0;
  }

  private getOfferHoldReason(): MeetingAutoStartSkipReason | undefined {
    if (config.meetings.disable_detection_notification) {
      return 'detection_notification_disabled';
    }
    if (meetingStore.recording.status !== 'idle') return 'already_recording';
    if (this.presenceLossStop.status === 'stopping') {
      return 'presence_loss_stop_in_progress';
    }
    if (this.presenceLossStop.status === 'notifying') {
      return 'presence_loss_notification_pending';
    }
    return undefined;
  }

  private async isPresenceLossNoticeUp(notificationId: string) {
    try {
      return await electronApis.lbNotifications.hasNotification(notificationId);
    } catch (error) {
      logError(
        error,
        '[meeting-app-detection] Failed to reconcile pending presence-loss notification'
      );
      return false;
    }
  }

  private listenForAppsUpdates() {
    if (this.appsReportedBeforeListening !== 'not-listening') return;
    this.appsReportedBeforeListening = 'asking';
    electronApis.meetings.listenForMeetingAppsUpdated((update) => {
      if (this.appsReportedBeforeListening !== 'answered') {
        this.appsReportedBeforeListening = 'superseded-by-live-report';
      }
      this.applyAppsUpdate(update);
    });
    void this.applyAppsReportedBeforeListening();
  }

  private async applyAppsReportedBeforeListening() {
    try {
      const update = await electronApis.meetings.getDetectedMeetingApps();
      const isSuperseded =
        this.appsReportedBeforeListening === 'superseded-by-live-report';
      if (!update || isSuperseded) return;
      this.applyAppsUpdate(update);
    } catch (error) {
      logError(
        error,
        '[meeting-app-detection] Failed to get the meeting apps reported before listening'
      );
    } finally {
      this.appsReportedBeforeListening = 'answered';
    }
  }

  private applyAppsUpdate({ apps, detectionId }: MeetingAppsUpdate) {
    const departedOffer = meetingAppDetections.update(apps);
    if (departedOffer) {
      meetingDebugStore.record({
        ...buildSkipEntry('app_detection', 'meeting_app_gone'),
        meetingId: null,
        detectionId,
      });
      withdrawMeetingAppOffer(departedOffer);
    }
    const cardAnsweredMeeting = meetingAppDetections.cardAnsweredMeeting();
    if (cardAnsweredMeeting && autoStartsOnDetection(cardAnsweredMeeting.app)) {
      void this.turnCardIntoCountdown(cardAnsweredMeeting.card, detectionId);
    }
    meetingDebugStore.record({
      kind: 'apps_updated',
      detectionId,
      summary: apps.length
        ? `Detected ${apps.map(describeDetectedApp).join(', ')}`
        : 'No meeting apps detected',
      fields: { apps, recordingStatus: meetingStore.recording.status },
    });
    for (const onAppsUpdated of this.connectedHooks) {
      onAppsUpdated({ apps, detectionId });
    }
  }

  private async turnCardIntoCountdown(
    card: {
      notificationId: string;
      autoStartAttemptId: string;
      isSent: boolean;
    },
    detectionId: string | undefined
  ) {
    const hasCardEnded =
      card.isSent && !(await this.isCardStillShown(card.notificationId));
    if (hasCardEnded) return;
    const withdrawnCard = meetingAppDetections.withdrawCardToOfferAgain(
      card.autoStartAttemptId
    );
    if (!withdrawnCard) return;
    withdrawMeetingAppOffer(withdrawnCard);
    await this.offerNext(detectionId);
  }

  private async isCardStillShown(notificationId: string) {
    try {
      return await electronApis.lbNotifications.hasNotification(notificationId);
    } catch (error) {
      logError(
        error,
        '[meeting-app-detection] Failed to check whether a card is still shown'
      );
      return false;
    }
  }

  private isPresenceLossNotice(notificationId: string) {
    return (
      this.presenceLossStop.status === 'notifying' &&
      this.presenceLossStop.notificationId === notificationId
    );
  }

  private pollForNotificationEnd(
    notificationId: string,
    isStillWatched: () => boolean,
    onEnd: () => Promise<void>
  ) {
    const notificationEndPoll = setInterval(() => {
      void (async () => {
        if (
          isStillWatched() &&
          (await electronApis.lbNotifications.hasNotification(notificationId))
        ) {
          return;
        }
        clearInterval(notificationEndPoll);
        if (isStillWatched()) await onEnd();
      })().catch((error) => {
        logError(
          error,
          '[meeting-app-detection] Failed to check whether a notification ended'
        );
      });
    }, NOTIFICATION_END_POLL_INTERVAL_MS);
  }
}

function nextAppToOffer() {
  return meetingAppDetections.nextAppToOffer({
    showsCards: settingsStore.lbNotificationsEnabled,
  });
}

function autoStartsOnDetection(app: DetectedMeetingApp) {
  return (
    !!config.meetings.auto_start_on_detection &&
    !subscriptionStore.hasReachedMeetingLimit &&
    !app.wasLiveWhenDetectionStarted
  );
}

function describeDetectedApp(app: DetectedMeetingApp) {
  const site = app.websiteHost ?? app.name;
  return isAnsweredMeeting(app) ? site : `${site} (${app.gateAnswer})`;
}

export const meetingAppOffers = new MeetingAppOffers();
